import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../store/store';
import {
  MEASUREMENT_CATALOG,
  CATALOG_BY_ID,
  MeasurementCategory,
  isAbnormal,
  normalizePatientSex,
  normalRangeFor,
} from '../pages/reports/measurementCatalog';
import { saveStructuredMeasurements } from '../rtk-apis/reports/saveStructuredMeasurements';
import { getStructuredMeasurements } from '../rtk-apis/reports/getStructuredMeasurements';
import { deriveSharedEchoValues } from '../Utils/deriveEchoValues';
import useDebounce from './useDebounce';

/**
 * Report-side structured echo measurement table (F7).
 *
 * Reads from the structured EchoMeasurement store (NOT templateHTML), renders the
 * catalog grouped by category, supports typed override, auto-computes derived rows,
 * flags abnormal values, and debounce-saves back to the same store the viewer panel
 * writes to. The report PDF renders from this store (per the agreed design).
 *
 * Read-only when the report is Finalized, or when ids are missing.
 */

const CATEGORY_ORDER: { key: MeasurementCategory; title: string }[] = [
  { key: '2D', title: '2D / Linear' },
  { key: 'MMODE', title: 'M-mode' },
  { key: 'DOPPLER', title: 'Doppler' },
  { key: 'STRAIN', title: 'Strain' },
  { key: 'DERIVED', title: 'Derived / Indexed' },
];

interface RowValue {
  value: number | null;
  source: 'CALIPER' | 'TYPED' | 'SR_IMPORT' | 'DERIVED';
  overridden?: boolean;
  method?: string;
  planeView?: string;
  annotationUID?: string;
  includeInReport?: boolean; // worksheet superset vs report subset; default true
}

type ValueMap = Record<string, RowValue>;

interface Props {
  reportId?: string;
  studyInstanceUID?: string;
  status?: string;
  /** Patient sex from the report record — drives sex-specific ASE reference bands.
   *  This table feeds the report PDF, so without it a woman's values are flagged
   *  against pooled limits in the document that actually gets signed and sent. */
  sex?: string | null;
}

export default function EchoMeasurementTable({
  reportId,
  studyInstanceUID,
  status,
  sex,
}: Props): React.ReactNode {
  const patientSex = normalizePatientSex(sex);
  const dispatch = useDispatch<AppDispatch>();
  const [values, setValues] = useState<ValueMap>({});
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const hydratedRef = useRef(false);

  const readOnly = status === 'Finalized' || !reportId || !studyInstanceUID;

  // ---- Hydrate from the structured store ----
  useEffect(() => {
    if (!reportId && !studyInstanceUID) return;
    (async () => {
      try {
        const res: any = await dispatch(
          getStructuredMeasurements({ reportId, studyInstanceUID })
        );
        const saved = res?.payload?.data?.measurements;
        if (Array.isArray(saved) && saved.length) {
          const next: ValueMap = {};
          saved.forEach((m: any) => {
            next[m.measurementId] = {
              value: typeof m.value === 'number' ? m.value : null,
              source: m.source || 'TYPED',
              overridden: m.overridden,
              method: m.method,
              planeView: m.planeView,
              annotationUID: m.annotationUID,
              includeInReport: m.includeInReport !== false,
            };
          });
          setValues(next);
        }
      } catch {
        /* no saved data yet */
      } finally {
        hydratedRef.current = true;
      }
    })();
  }, [reportId, studyInstanceUID, dispatch]);

  // ---- Derived values (same engine as the viewer panel) ----
  const derived = useMemo<ValueMap>(() => {
    const v = (id: string) => values[id]?.value ?? null;
    const out: ValueMap = {};
    const set = (id: string, val: number | null) => {
      if (val !== null) out[id] = { value: Number(val.toFixed(2)), source: 'DERIVED' };
    };
    Object.entries(
      deriveSharedEchoValues(v, {
        isTypedOverride: (id: string) =>
          values[id]?.source === 'TYPED' || values[id]?.overridden === true,
        allowGeneralDiastolicGrade: v('DIASTOLIC_GENERAL_PATHWAY_CONFIRMED') === 1,
      })
    ).forEach(([id, val]) => set(id, val));
    return out;
  }, [values]);

  const merged = useMemo<ValueMap>(() => {
    const { DIAST_GRADE: _staleDerivedGrade, ...primaryValues } = values;
    return { ...derived, ...primaryValues };
  }, [values, derived]);

  const onType = (measurementId: string, raw: string) => {
    if (readOnly) return;
    if (CATALOG_BY_ID[measurementId]?.importOnly) return;
    const parsed = raw === '' ? null : Number(raw);
    setValues(prev => ({
      ...prev,
      [measurementId]: {
        ...prev[measurementId],
        value: Number.isFinite(parsed as number) ? (parsed as number) : null,
        source: 'TYPED',
        overridden: true,
        includeInReport: prev[measurementId]?.includeInReport ?? true,
      },
    }));
  };

  // Toggle whether a measurement is carried into the report / PDF / GP view.
  // Worksheet keeps every value; the printed report is the curated subset.
  const onToggleInclude = (measurementId: string, include: boolean) => {
    if (readOnly) return;
    setValues(prev => {
      const base = prev[measurementId] || merged[measurementId];
      if (!base) return prev;
      return {
        ...prev,
        [measurementId]: { ...base, includeInReport: include },
      };
    });
  };

  // ---- Debounced persistence ----
  const debouncedValues = useDebounce(merged, 2000);
  useEffect(() => {
    if (readOnly || !hydratedRef.current || !reportId || !studyInstanceUID) return;
    const payloadMeasurements = Object.entries(debouncedValues)
      .filter(([, rv]) => rv && rv.value !== null && rv.value !== undefined)
      .map(([measurementId, rv]) => {
        const entry = CATALOG_BY_ID[measurementId];
        return {
          measurementId,
          parameterName: entry?.label || measurementId,
          category: (entry?.category || 'DERIVED') as MeasurementCategory,
          value: rv.value,
          unit: entry?.unit || '',
          method: rv.method,
          planeView: rv.planeView,
          source: rv.source,
          annotationUID: rv.annotationUID,
          loincCode: entry?.loincCode,
          normalRange: normalRangeFor(measurementId, patientSex),
          isAbnormal: isAbnormal(measurementId, rv.value, patientSex),
          overridden: rv.overridden,
          includeInReport: entry?.workflowControl ? false : rv.includeInReport !== false,
        };
      });
    if (!payloadMeasurements.length) return;
    setSaveState('saving');
    dispatch(
      saveStructuredMeasurements({
        reportId,
        studyInstanceUID,
        measurements: payloadMeasurements,
      })
    )
      .then((r: any) => setSaveState(r?.payload?.status === 'SUCCESS' ? 'saved' : 'error'))
      .catch(() => setSaveState('error'));
  }, [debouncedValues, reportId, studyInstanceUID, readOnly, dispatch, patientSex]);

  return (
    <div className="echo-measurement-table">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-lg font-semibold">Echo Measurements</h3>
        <span className="text-sm text-gray-500">
          {readOnly
            ? status === 'Finalized'
              ? 'Finalized (read-only)'
              : 'Read-only'
            : saveState === 'saving'
              ? 'Saving…'
              : saveState === 'saved'
                ? 'Saved'
                : saveState === 'error'
                  ? 'Save failed'
                  : ''}
        </span>
      </div>

      {CATEGORY_ORDER.map(({ key, title }) => {
        const rows = MEASUREMENT_CATALOG.filter(e => e.category === key && !e.workflowControl);
        if (!rows.length) return null;
        return (
          <div key={key} className="mb-4">
            <div className="mb-1 text-sm font-bold uppercase tracking-wide text-gray-600">
              {title}
            </div>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-gray-500">
                  <th className="py-1 pr-2">Parameter</th>
                  <th className="py-1 pr-2">Value</th>
                  <th className="py-1 pr-2">Unit</th>
                  <th className="py-1 pr-2">Normal</th>
                  <th className="py-1 pr-2 text-center" title="Include this measurement in the report PDF">
                    In report
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map(entry => {
                  const rv = merged[entry.measurementId];
                  const value = rv?.value ?? null;
                  const importOnly = entry.importOnly === true;
                  const abnormal = isAbnormal(entry.measurementId, value, patientSex);
                  const coded =
                    entry.valueLabels && value != null
                      ? entry.valueLabels[value] ?? String(value)
                      : null;
                  const nr = entry.valueLabels ? undefined : entry.normalRange;
                  const nrText = nr
                    ? `${nr.low ?? ''}${nr.low !== undefined && nr.high !== undefined ? '–' : ''}${
                        nr.high ?? ''
                      }`
                    : '';
                  return (
                    <tr key={entry.measurementId} className="border-b">
                      <td
                        className="py-1 pr-2 text-gray-700"
                        title={importOnly ? 'Import only — requires a validated speckle-tracking result' : undefined}
                      >
                        {entry.label}{importOnly ? ' (import only)' : ''}
                      </td>
                      <td className="py-1 pr-2">
                        {entry.derived ? (
                          <span className={abnormal ? 'font-semibold text-red-600' : ''}>
                            {coded ?? value ?? '—'}
                          </span>
                        ) : (
                          <input
                            type="number"
                            step="any"
                            disabled={readOnly || importOnly}
                            title={importOnly ? 'Import only — requires a validated speckle-tracking result' : undefined}
                            value={value ?? ''}
                            onChange={e => onType(entry.measurementId, e.target.value)}
                            className={`w-24 rounded border px-1 py-0.5 text-right ${
                              abnormal ? 'border-red-400 text-red-600' : ''
                            } ${readOnly || importOnly ? 'bg-gray-100' : ''}`}
                          />
                        )}
                      </td>
                      <td className="py-1 pr-2 text-xs text-gray-500">{entry.unit}</td>
                      <td className="py-1 pr-2 text-xs text-gray-400">{nrText}</td>
                      <td className="py-1 pr-2 text-center">
                        {value !== null && value !== undefined ? (
                          <input
                            type="checkbox"
                            disabled={readOnly}
                            checked={(rv?.includeInReport ?? true) !== false}
                            onChange={e => onToggleInclude(entry.measurementId, e.target.checked)}
                            title={
                              (rv?.includeInReport ?? true) !== false
                                ? 'Shown in the report — uncheck to exclude'
                                : 'Excluded from the report — check to include'
                            }
                          />
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}
